import type { SyntheticEvent } from 'react';
import { hasTintedBackground } from '../../BannerComponents/BannerUtility';
import type { Entry } from 'contentful';
import type { ImageContentType } from 'tsconfig/types';
import type { Theme } from '@emotion/react';

export const navigateToBannerLink = (link?: string): void => {
  if (link) {
    window.location.assign(link);
  }
};

export const stopBannerContentEventPropagation = (event: SyntheticEvent): void => {
  if ((event.nativeEvent.target as HTMLInputElement).tagName === 'A') {
    event.stopPropagation();
  }
};

export const buildDLClickEventHandler = (
  e?: React.MouseEvent | React.KeyboardEvent,
  dlClickEventHandler?: (data: string) => void,
  entryTitle?: string | undefined,
  bannerLink?: string | undefined,
  bannerTitle?: string | undefined,
): void => {
  if (!bannerLink) {
    return;
  }
  const clickedElement = e?.target as HTMLElement;
  const cardTitle = bannerTitle || entryTitle || '';
  let clickText = '',
    clickName = '';
  if (!clickedElement) {
    navigateToBannerLink(bannerLink);
    return;
  }
  if (clickedElement.tagName === 'IMG') {
    clickText = clickedElement.getAttribute('alt')?.trim() || 'not set';
    clickName = 'image';
  } else if (clickedElement.tagName === 'BUTTON' || clickedElement.tagName === 'A') {
    clickText = clickedElement.textContent?.trim() || '';
    clickName = 'button';
  } else if (cardTitle) {
    clickText = cardTitle;
    clickName = 'headline';
  }

  if (clickText && dlClickEventHandler) {
    dlClickEventHandler(`${clickText}:${clickName}:${cardTitle}:${bannerLink}`);
  }
  navigateToBannerLink(bannerLink);
};

export const getBannerTitle = (bannerContent: string): string => {
  if (!bannerContent) {
    return '';
  }

  // Use a simpler regex pattern that matches h2 tag with any content (including nested tags)
  // The non-greedy .*? ensures we match up to the first closing h2 tag
  const h2Match = /<h2[^>]*>([\s\S]*?)<\/h2>/i.exec(bannerContent);

  if (!h2Match?.[1]) {
    return '';
  }

  // Strip all HTML tags and trim whitespace
  return h2Match[1]
    .split(/<[^>]*>/)
    .join('')
    .trim();
};

export const getButtonClass = (classVariant: string): string => {
  switch (classVariant) {
    case 'Secondary button':
      return 'darkColor';
    case 'Secondary-inverted button':
      return 'lightColor';
    default:
      return '';
  }
};

// Helper functions to reduce complexity
export const getImageStyles = (
  tintedBackground: boolean,
  image?: Entry<ImageContentType, 'WITHOUT_UNRESOLVABLE_LINKS', string>,
  backgroundImageUrl?: string,
  theme?: Theme,
): string => {
  if (tintedBackground) {
    // Target mobile and tablet portrait devices using md breakpoint (below desktop)
    const mdBreakpoint = theme?.breakpointWidths?.md;
    return `@media (max-width: ${mdBreakpoint}px) {  
              padding: 20px 0px 20px 0px;
              display: flex;
              justify-content: center;
              min-height: 400px;
              height: auto;
            }`;
  } else if (image) {
    return `background: url(${backgroundImageUrl});
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center;`;
  }
  return '';
};

export const getBannerImageStyles = (styles: string, minimumBannerHeight: number | undefined, theme: Theme): string =>
  `
  ${styles}
  ${theme.breakpoints.md} {
    ${minimumBannerHeight && `min-height: ${minimumBannerHeight}px`};
  }
  min-width: 100%;
  position: relative;
  ${theme.breakpoints.xs} {
    min-width: auto;
  }
  .banner-imgcomp {
    position: relative;
    display: flex;
    justify-content: center;
    z-index: 1;
  }
  .banner-bg-img {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    .cms-image-container {
      flex: 1;
    }
  }
`;

export const getVideoStyles = (
  largeDeviceVimeoVideo: string | undefined,
  variant: string,
  theme: Theme,
  videoAspectRatio: number,
): string => {
  if (!largeDeviceVimeoVideo) {
    return '';
  }

  if (hasTintedBackground(variant)) {
    return `
      ${theme.widgets.CmsBanner?.ExternalVideoBackgroundTintedVariant};
      padding-top: ${videoAspectRatio * 100}%;
    `;
  }
  return `
    .external-video-player {
      padding-top: ${videoAspectRatio * 100}%;
      ${theme.widgets.CmsBanner?.ExternalVideoBackgroundContenVariant}
    }
  `;
};

export const getBackgroundStyles = (
  tintedBackground: boolean,
  contentBackgroundColour: string,
  theme: Theme,
): string => {
  if (tintedBackground) {
    return 'z-index: 1;';
  }
  return `
    background: ${
      contentBackgroundColour
        ? (theme.colors.bgColors as unknown as Record<string, string>)[contentBackgroundColour]
        : 'initial'
    };
  `;
};

export const getContentType = (
  backgroundImage?: Entry<ImageContentType, 'WITHOUT_UNRESOLVABLE_LINKS', string>,
): string => {
  if (backgroundImage?.fields?.largeDeviceImage?.fields?.file?.contentType?.includes('video')) {
    return 'largeVideo';
  } else if (backgroundImage?.fields?.smallDeviceImage?.fields?.file?.contentType?.includes('video')) {
    return 'smallVideo';
  } else if (backgroundImage?.fields?.largeDeviceImage?.fields?.file?.contentType?.includes('image')) {
    return 'largeImage';
  } else if (backgroundImage?.fields?.smallDeviceImage?.fields?.file?.contentType?.includes('image')) {
    return 'smallImage';
  }
  return '';
};
